import {createSelector} from 'reselect';

import DEVICE_TYPES from '../../enums/device/deviceTypes';
import ICC_PRODUCT_STATUS_TYPES from '../../enums/icc/iccProductStatusTypes';
import devicePlatformService from '../../services/device/devicePlatformService';
import deviceTypesService from '../../services/device/deviceTypesService';
import marketConfigService from '../../services/marketConfigService';
import marketTypeService from '../../services/marketTypeService';
import productHelpers from '../../services/product/productHelpers';
import productService from '../../services/product/productService';
import yapService from '../../services/yap/yapService';
import {selectDeviceActivationStatus, selectIotDeviceData} from './iotDevice';

const selectConsumer = (state) => state.consumer;

const selectConsumerProducts = createSelector(selectConsumer, (state) => state.products);

const selectMuleSoftConsumerProducts = createSelector(selectConsumer, (state) => state.muleSoftProductsAsset);

const selectConsumerData = createSelector(selectConsumer, (state) => state.data);

const selectConsumerProductsInfo = createSelector(selectConsumer, (state) => state.productsInfo);

const selectConsumerProductsStatus = createSelector(selectConsumer, (state) => state.productsStatus);

const selectConsumerRecentlyPairedDevices = createSelector(selectConsumer, (state) => state.recentlyPairedDevices);

const selectConsumerTnc = createSelector(selectConsumer, (state) => state.tnc);

const selectConsumerAcceptedTnc = createSelector(selectConsumer, (state) => state.acceptedTnc);

const makeSelectIsMuleSoftConsumerProductRegistered = (codentify) =>
    createSelector(selectMuleSoftConsumerProducts, (muleSoftConsumerProducts) => {
        return muleSoftConsumerProducts.some(
            (dmConsumerProduct) =>
                dmConsumerProduct.codentify === codentify || dmConsumerProduct.packCodentify === codentify
        );
    });

const makeSelectLinkedMuleSoftConsumerProductsByCodentify = (codentify) =>
    createSelector(selectMuleSoftConsumerProducts, (muleSoftConsumerProducts) => {
        return muleSoftConsumerProducts.filter(
            (dmConsumerProduct) =>
                dmConsumerProduct.codentify === codentify || dmConsumerProduct.packCodentify === codentify
        );
    });

const selectConsumerId = createSelector(selectConsumerData, (data) => data?.consumerId);

const makeSelectConsumerFirstName = () => createSelector(selectConsumerData, (data) => data?.firstName);

const makeSelectConsumerProduct = ({codentify, deviceSerialNumber}) =>
    createSelector(selectConsumerProducts, (products) =>
        productHelpers.forceFindProduct({
            products,
            deviceSerialNumber,
            codentify,
        })
    );

const makeSelectConsumerInfoProduct = (deviceSerialNumber) =>
    createSelector(selectConsumerProductsInfo, (products) =>
        productHelpers.forceFindProduct({
            products,
            deviceSerialNumber,
        })
    );

const makeSelectConsumerProductsMergedInfo = () =>
    createSelector(
        selectConsumerProducts,
        selectMuleSoftConsumerProducts,
        selectConsumerProductsInfo,
        (products, muleSoftProducts, productsInfo) => {
            if (!productsInfo) return;

            const isDCE2 = marketTypeService.isDCE2();

            if (isDCE2) {
                const productsIds = muleSoftProducts?.map(({deviceSerialNumber}) => deviceSerialNumber);
                const filteredProductsInfo =
                    productsInfo.filter(
                        (product) =>
                            !product.isHolder &&
                            product.type !== DEVICE_TYPES.DEV_TYPE_UNKNOWN &&
                            productsIds.includes(product.deviceSerialNumber)
                    ) || [];

                const mergedProducts = filteredProductsInfo.map((x) => {
                    const productInfo = products?.find(
                        (product) => x.deviceSerialNumber === product.deviceSerialNumber
                    );

                    x.productName = productInfo?.productName;

                    return x;
                });

                return mergedProducts;
            }

            if (!products?.length) return [];

            const registeredProducts = products.filter(
                (product) => !product.isHolder && product.status === ICC_PRODUCT_STATUS_TYPES.REGISTERED
            );

            const consumerProductMergedInfo = registeredProducts.map((product) => {
                const productInfo = productsInfo.find(
                    (productInfo) => productInfo.deviceSerialNumber === product.deviceSerialNumber
                );
                const {colorHex, mediaId, brandFamily, defaultProductName, type} = productInfo || {};
                const productType = type
                    ? type
                    : devicePlatformService.getDeviceTypeBySerialNumber(product.deviceSerialNumber);

                return {
                    ...product,
                    colorHex,
                    mediaId,
                    brandFamily,
                    defaultProductName: defaultProductName
                        ? defaultProductName
                        : deviceTypesService.getProductNameByType(productType),
                    isDeviceSupported: marketConfigService.isDeviceSupported(productType),
                    type: productType,
                };
            });

            return consumerProductMergedInfo.filter((product) => product.type !== DEVICE_TYPES.DEV_TYPE_UNKNOWN);
        }
    );

const makeSelectIotDeviceMergedWithConsumerProduct = () =>
    createSelector(
        selectIotDeviceData,
        selectConsumerProducts,
        selectMuleSoftConsumerProducts,
        selectConsumerProductsInfo,
        selectDeviceActivationStatus,
        (iotDevice, iccProducts, muleSoftProducts, productsInfoList, isDeviceActivated) => {
            if (iotDevice?.device) {
                const device = {...iotDevice.device};
                const holder = {...iotDevice.holder};
                const {isP4, isP1V4, codentifyOriginal, codentify: iotDeviceCodentifyId} = device;
                const isDCE2 = marketTypeService.isDCE2();
                const products = isDCE2 ? muleSoftProducts : iccProducts;

                const consumerProduct = productHelpers.forceFindProduct({
                    products,
                    deviceSerialNumber: iotDevice.deviceSerialNumber,
                    codentify: codentifyOriginal,
                });

                const iccConsumerProduct = productHelpers.forceFindProduct({
                    products: iccProducts,
                    deviceSerialNumber: iotDevice.deviceSerialNumber,
                    codentify: codentifyOriginal,
                });

                const productCodentifyId = consumerProduct?.codentify;

                if (isP4) {
                    device.codentify = iotDeviceCodentifyId || productCodentifyId;
                } else {
                    device.codentify = productCodentifyId || iotDeviceCodentifyId;
                }

                const getOriginalCodentify = (deviceSerialNumber) => {
                    const consumerInfoProduct = productHelpers.forceFindProduct({
                        products: productsInfoList,
                        deviceSerialNumber,
                    });

                    return consumerInfoProduct?.codentifyID;
                };

                device.codentifyOriginal =
                    device.codentifyOriginal ||
                    getOriginalCodentify(iotDevice.deviceSerialNumber) ||
                    productCodentifyId;

                if (consumerProduct) {
                    device.productId = consumerProduct.productId;

                    if (isDCE2) {
                        device.status = ICC_PRODUCT_STATUS_TYPES.REGISTERED;
                    } else {
                        device.status = consumerProduct.status;
                    }
                }

                if (holder) {
                    const consumerProductHolder = productHelpers.forceFindProduct({
                        products,
                        deviceSerialNumber: iotDevice.holder?.deviceSerialNumber,
                    });

                    holder.codentifyOriginal =
                        holder.codentifyOriginal ||
                        getOriginalCodentify(holder.deviceSerialNumber) ||
                        consumerProductHolder?.codentify;
                    holder.name =
                        consumerProductHolder?.productName || deviceTypesService.getProductNameByType(holder.type);

                    if (consumerProductHolder) {
                        holder.codentify = consumerProductHolder.codentify || holder.codentify;
                        holder.productId = consumerProductHolder.productId;

                        if (isDCE2) {
                            holder.status = ICC_PRODUCT_STATUS_TYPES.REGISTERED;
                        } else {
                            holder.status = consumerProductHolder.status;
                        }
                    }
                }

                const iotDeviceType = device?.type;
                const productName = iccConsumerProduct?.productName;

                const isYAPFlowEnabled = yapService.isYapMode();

                device.name = productName ? productName : deviceTypesService.getProductNameByType(iotDeviceType);
                device.isDeviceActivated = (isP4 || isP1V4) && isYAPFlowEnabled ? isDeviceActivated : true;
                device.isP4 = isP4;
                device.isP1V4 = isP1V4;

                return {...iotDevice, device, holder};
            } else {
                return iotDevice;
            }
        }
    );

const makeSelectIsIotProductRegistered = (isHolder) =>
    createSelector(makeSelectIotDeviceMergedWithConsumerProduct(), (iotDevice) => {
        const {device, holder} = iotDevice || {};
        const target = isHolder ? holder : device;
        const {status} = target || {};

        return productService.getIsRegistered(status);
    });

const makeSelectTncByType = (type) =>
    createSelector(selectConsumerTnc, (tnc) => (tnc ? tnc.find((item) => item.screenId === type) : {}));

const makeSelectAcceptedTncByType = (type) =>
    createSelector(selectConsumerAcceptedTnc, (acceptedTnc) =>
        acceptedTnc ? acceptedTnc.find((item) => item.screenId === type) : {}
    );

const makeSelectTncLegals = () =>
    createSelector(selectConsumerTnc, selectConsumerAcceptedTnc, (tnc, acceptedTnc) => {
        const legals = filterLegals(tnc);
        const acceptedLegals = filterLegals(acceptedTnc);

        return [...legals, ...acceptedLegals];
    });

const makeSelectTncLegalsByType = (type) =>
    createSelector(makeSelectTncByType(type), (tnc) => {
        return tnc?.legals || [];
    });

const makeSelectLegalBySettingsCode = (codes) =>
    createSelector(makeSelectTncLegals(), (legals) => {
        return legals?.find((legal) => legal.settings?.split(',').some((code) => codes.includes(code)));
    });

const filterLegals = (tnc) => {
    return tnc?.length
        ? tnc
              .map(({legals}) => {
                  return legals ? [...legals] : [];
              })
              .reduce((prev, next) => [...prev, ...next])
        : [];
};

const makeSelectProductStatus = (deviceSerialNumber, codentify) =>
    createSelector(selectConsumerProductsStatus, (products) =>
        productHelpers.forceFindProduct({products, deviceSerialNumber, codentify})
    );

const makeSelectConsumerProductsIds = (deviceTypeService) =>
    createSelector(selectConsumerProducts, selectConsumerProductsInfo, (products, productsInfo) => {
        const registeredProducts = products.filter((product) => {
            return product.status === ICC_PRODUCT_STATUS_TYPES.REGISTERED && deviceTypeService(product.type);
        });

        const mapCodentifyAndSerialNumber = (product) => {
            const {codentify, deviceSerialNumber} = product;

            if (productHelpers.isOriginalCodentifyValid(codentify)) {
                return {codentifyID: codentify, deviceSerialNumber};
            }

            const productInfo = productsInfo?.find((productInfo) => {
                const {type, codentifyID} = productInfo;

                return (
                    productInfo.deviceSerialNumber === deviceSerialNumber &&
                    type !== DEVICE_TYPES.DEV_TYPE_UNKNOWN &&
                    codentifyID &&
                    deviceTypeService(type)
                );
            });

            if (!productInfo) return null;

            return {codentifyID: productInfo.codentifyID, deviceSerialNumber};
        };

        const productsIds = registeredProducts.map(mapCodentifyAndSerialNumber).filter((product) => product);

        return productsIds;
    });

const makeSelectConsumerProductsWithWrongCodentifyId = (deviceTypeService) =>
    createSelector(
        selectConsumerProducts,
        makeSelectConsumerProductsIds(deviceTypeService),
        (products, productsIds) => {
            const registeredProducts = products.filter((product) => {
                return product.status === ICC_PRODUCT_STATUS_TYPES.REGISTERED && deviceTypeService(product.type);
            });
            const serialNumbers = productsIds.map(({deviceSerialNumber}) => deviceSerialNumber);
            const deviceSerialNumbers = registeredProducts.filter(
                ({deviceSerialNumber}) => !serialNumbers.includes(deviceSerialNumber)
            );

            return deviceSerialNumbers;
        }
    );

const makeSelectDeviceName = () =>
    createSelector(makeSelectIotDeviceMergedWithConsumerProduct(), (iotDevice) => iotDevice?.device?.name || '');

const makeSelectHolderName = () =>
    createSelector(makeSelectIotDeviceMergedWithConsumerProduct(), (iotDevice) => iotDevice?.holder?.name || '');

export {
    makeSelectAcceptedTncByType,
    makeSelectConsumerFirstName,
    makeSelectConsumerInfoProduct,
    makeSelectConsumerProduct,
    makeSelectConsumerProductsIds,
    makeSelectConsumerProductsMergedInfo,
    makeSelectConsumerProductsWithWrongCodentifyId,
    makeSelectDeviceName,
    makeSelectHolderName,
    makeSelectIotDeviceMergedWithConsumerProduct,
    makeSelectIsIotProductRegistered,
    makeSelectIsMuleSoftConsumerProductRegistered,
    makeSelectLegalBySettingsCode,
    makeSelectLinkedMuleSoftConsumerProductsByCodentify,
    makeSelectProductStatus,
    makeSelectTncByType,
    makeSelectTncLegals,
    makeSelectTncLegalsByType,
    selectConsumerAcceptedTnc,
    selectConsumerData,
    selectConsumerId,
    selectConsumerProducts,
    selectConsumerProductsInfo,
    selectConsumerRecentlyPairedDevices,
    selectConsumerTnc,
    selectMuleSoftConsumerProducts,
};
